import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { 
  Rocket, 
  ArrowRight, 
  Zap, 
  Database, 
  Building2, 
  BadgeCheck,
  Sparkles,
  ChevronRight,
  Star,
  Users,
  CheckCircle2,
  TrendingUp
} from "lucide-react";

import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";

const products = [
  {
    id: "ssm",
    title: "SSM",
    category: "Growth Suite",
    tagline: "AI SEO, Blog & Social Media Automation",
    description:
      "Get Found. Stay Active. Generate More Leads.",
    body:
      "Automate SEO, blog publishing, and social media content without hiring a full marketing team.",
    href: "https://ssm.oratorhype.com/",
    cta: "Explore Product",
    icon: Rocket,
    features: ["AI Content Generation", "Automated Publishing", "SEO Optimization", "Social Media Scheduling"]
  },
  {
    id: "omniflow",
    title: "OmniFlow CRM",
    category: "Business Suite",
    tagline: "CRM • Vendor • Procurement • Billing",
    description:
      "Run Your Entire Business From One Dashboard.",
    body:
      "Manage leads, customers, vendors, procurement, billing, and GST operations efficiently.",
    href: "https://crm.oratorhype.com/",
    cta: "Explore Product",
    icon: Building2,
    features: ["Lead Management", "Vendor Portal", "Procurement", "GST Billing"]
  },
  {
    id: "vendoros",
    title: "VendorOS",
    category: "Marketplace Suite",
    tagline: "Register Your Company & Grow Your Sales",
    description:
      "List Your Business. Let Us Market It.",
    body:
      "Join our vendor ecosystem and connect your products with potential customers and business partners.",
    href: "https://vendoros.oratorhype.com/",
    cta: "Explore Product",
    icon: Database,
    features: ["Business Listing", "Product Showcase", "Lead Generation", "Analytics Dashboard"]
  },
  {
    id: "coming",
    title: "More Products Coming Soon",
    category: "Launching Soon",
    tagline: "",
    description: "",
    body: "",
    href: null,
    cta: "Explore Product",
    icon: Zap,
    comingSoon: true,
    badge: "Launching Soon",
    upcoming: [
      "Marketing Automation",
      "HR & Payroll",
      "Analytics AI",
      "Business Intelligence",
      "Additional business solutions",
    ],
  },
];

const Products = () => {
  return (
    <>
      <Helmet>
        <title>Products - Orator Hype</title>
        <meta
          name="description"
          content="Explore Orator's ecosystem of business solutions: SSM, OmniFlow CRM, VendorOS and more launching soon."
        />
      </Helmet>

      <div className="min-h-screen bg-[#0a0e1a] text-white overflow-x-hidden">
        <Header />

        <main>
          {/* Hero Section - Full Width Background Image */}
          <section 
            className="relative overflow-hidden min-h-[90vh] flex items-center"
            style={{
              backgroundImage: 'url("/public/hero_section/OUR PRODUCT HOME BANNER.png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e1a]/95 via-[#0a0e1a]/80 to-[#0a0e1a]/90" />
            
            {/* Animated Glow Effects */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl animate-pulse" />
              <div className="absolute top-20 left-1/4 w-80 h-80 rounded-full bg-purple-500/20 blur-3xl animate-pulse delay-1000" />
              <div className="absolute -bottom-40 left-1/3 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl animate-pulse delay-2000" />
            </div>

            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-white/90">
                      Orator Ecosystem
                    </span>
                  </div>

                  <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-tight">
                    <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                      Solutions That Drive
                    </span>
                    <br />
                    <span className="text-white">Business Growth</span>
                  </h1>
                  
                  <p className="mt-6 text-base sm:text-lg md:text-xl text-gray-300/90 max-w-2xl mx-auto leading-relaxed">
                    Generate more leads, automate operations, and scale faster with Orator's ecosystem of AI-powered business solutions.
                  </p>

                  <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    {["AI-first", "SaaS-ready", "Built for scale", "Enterprise-grade"].map((x) => (
                      <motion.div
                        key={x}
                        whileHover={{ scale: 1.05 }}
                        className="px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm text-gray-200/90 hover:bg-white/20 hover:border-white/30 transition-all cursor-default backdrop-blur-md"
                      >
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />
                          {x}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  {/* <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-10 flex items-center justify-center gap-8 flex-wrap"
                  >
                    <div className="flex -space-x-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="w-10 h-10 rounded-full border-2 border-[#0a0e1a] bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-xs font-bold">
                          {String.fromCharCode(64 + i)}
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white/90">Trusted by 500+</p>
                      <p className="text-xs text-gray-400">Growing businesses worldwide</p>
                    </div>
                  </motion.div> */}

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-10 flex flex-wrap gap-4 justify-center"
                  >
                    <motion.a
                      href="#products"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-extrabold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 hover:from-blue-500 hover:to-blue-400"
                    >
                      Explore Products
                      <ArrowRight className="w-5 h-5" />
                    </motion.a>
                    
                    <motion.a
                      href="#"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white font-semibold hover:bg-white/20 transition-all duration-300"
                    >
                      Learn More
                    </motion.a>
                  </motion.div>

                  {/* Floating Stats */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto"
                  >
                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                      <div className="text-2xl font-bold text-blue-400">50K+</div>
                      <div className="text-xs text-gray-400 mt-1">Leads Generated</div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                      <div className="text-2xl font-bold text-purple-400">98%</div>
                      <div className="text-xs text-gray-400 mt-1">Satisfaction Rate</div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                      <div className="text-2xl font-bold text-cyan-400">24/7</div>
                      <div className="text-xs text-gray-400 mt-1">Support Available</div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                      <div className="text-2xl font-bold text-amber-400">3x</div>
                      <div className="text-xs text-gray-400 mt-1">Faster Growth</div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Products Grid - Enhanced */}
          <section id="products" className="py-16 md:py-20 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-transparent to-[#0a0e1a] pointer-events-none" />
            
            <div className="container mx-auto px-4 md:px-6 lg:px-8 relative">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-4">
                  <Star className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/90">Our Products</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
                  <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                    Powerful Business Solutions
                  </span>
                </h2>
                <p className="mt-4 text-gray-300/90 max-w-2xl mx-auto text-lg">
                  Explore Orator's ecosystem of AI-powered solutions designed to accelerate your business growth.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {products.slice(0, 3).map((p, index) => {
                  const Icon = p.icon;
                  return (
                    <motion.article
                      key={p.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -8, transition: { duration: 0.2 } }}
                      className="group relative rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-xl overflow-hidden hover:border-white/20 transition-all duration-300"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="p-7 md:p-8 relative">
                        <div className="flex items-center justify-between gap-4">
                          <motion.div 
                            whileHover={{ rotate: 360, scale: 1.1 }}
                            transition={{ duration: 0.6 }}
                            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-3 flex items-center justify-center shadow-lg shadow-blue-500/20"
                          >
                            <Icon className="w-7 h-7 text-white" />
                          </motion.div>
                          <div className="text-xs font-semibold text-amber-200/90 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full">
                            {p.category}
                          </div>
                        </div>

                        <h3 className="mt-5 text-2xl font-extrabold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-blue-200 group-hover:bg-clip-text transition-all duration-300">
                          {p.title}
                        </h3>
                        <p className="mt-2 text-sm text-blue-200/90 font-medium">{p.tagline}</p>

                        <p className="mt-4 text-white/90 font-semibold leading-relaxed text-lg">
                          {p.description}
                        </p>
                        <p className="mt-2 text-gray-300/90 text-sm leading-relaxed">
                          {p.body}
                        </p>

                        {/* Feature Chips */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {p.features?.slice(0, 3).map((feature) => (
                            <span key={feature} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300/80">
                              {feature}
                            </span>
                          ))}
                          {p.features?.length > 3 && (
                            <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300/80">
                              +{p.features.length - 3}
                            </span>
                          )}
                        </div>

                        <div className="mt-6 flex flex-col gap-2">
                          <motion.a
                            href={p.href}
                            target="_blank"
                            rel="noreferrer"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-extrabold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 hover:from-blue-500 hover:to-blue-400"
                          >
                            {p.cta}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </motion.a>
                          
                          <a
                            href={p.href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-cyan-300/80 hover:text-cyan-200 underline underline-offset-4 transition-colors text-center"
                          >
                            {p.href.replace('https://', '')}
                          </a>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}

                {/* Coming Soon Card - Enhanced */}
                <motion.article
                  key={products[3].id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  whileHover={{ y: -8 }}
                  className="group relative lg:col-span-1 rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm shadow-xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="p-7 md:p-8 relative">
                    <div className="flex items-center justify-between gap-4">
                      <motion.div 
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                        className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-3 flex items-center justify-center shadow-lg shadow-blue-500/20"
                      >
                        <Zap className="w-7 h-7 text-white" />
                      </motion.div>
                      <div className="text-xs font-semibold text-amber-200/90 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full animate-pulse">
                        {products[3].badge}
                      </div>
                    </div>

                    <h3 className="mt-5 text-2xl font-extrabold text-transparent bg-gradient-to-r from-amber-200 to-orange-200 bg-clip-text">
                      {products[3].title}
                    </h3>

                    <div className="mt-6 space-y-3">
                      {products[3].upcoming.map((item, idx) => (
                        <motion.div 
                          key={item} 
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="flex items-center gap-3 group/item"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400" />
                          <span className="text-sm text-gray-300/90 group-hover/item:text-white transition-colors">
                            {item}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    <div className="mt-6">
                      <button
                        disabled
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white/60 font-extrabold cursor-not-allowed opacity-50"
                      >
                        <span className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          Coming Soon
                          <Sparkles className="w-4 h-4" />
                        </span>
                      </button>
                    </div>
                  </div>
                </motion.article>
              </div>

              {/* Bottom CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-16 text-center"
              >
                <div className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                  <Users className="w-5 h-5 text-blue-400" />
                  {/* <span className="text-sm text-gray-300/90">
                    Join 500+ businesses already growing with Orator
                  </span> */}
                  <ChevronRight className="w-4 h-4 text-blue-400" />
                </div>
              </motion.div>
            </div>
          </section>

          <div className="h-4" />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Products;